// =============================================================================
// NotificationBanners — global dashboard banner slot
// =============================================================================
// Live-reads the active banner broadcasts targeted at the current user and
// renders them above the page content. Dismissing a banner persists the choice
// server-side so it stays gone across sessions/devices. The banner body is
// admin HTML already sanitized on the server before storage.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { notificationKeys, type Banner } from '@/lib/notifications';

const TONE_STYLES: Record<string, string> = {
  info: 'border-brand-200 bg-brand-50 text-brand-800',
  success: 'border-success-200 bg-success-50 text-success-800',
  warning: 'border-yellow-200 bg-yellow-50 text-yellow-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
};

export function NotificationBanners() {
  const queryClient = useQueryClient();

  const bannersQuery = useQuery<Banner[], Error>({
    queryKey: notificationKeys.banners,
    queryFn: () => api.get<Banner[]>('/api/v1/notifications/banners'),
    staleTime: 60_000,
  });

  const dismiss = useMutation<unknown, Error, string>({
    mutationFn: (id) => api.post(`/api/v1/notifications/banners/${id}/dismiss`, {}),
    onMutate: async (id) => {
      // Optimistically remove the banner so it disappears immediately.
      await queryClient.cancelQueries({ queryKey: notificationKeys.banners });
      const prev = queryClient.getQueryData<Banner[]>(notificationKeys.banners);
      queryClient.setQueryData<Banner[]>(notificationKeys.banners, (cur) =>
        (cur ?? []).filter((b) => b.id !== id),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      const prev = (ctx as { prev?: Banner[] } | undefined)?.prev;
      if (prev) queryClient.setQueryData(notificationKeys.banners, prev);
    },
  });

  const banners = bannersQuery.data ?? [];
  if (banners.length === 0) return null;

  return (
    <div className="space-y-2">
      {banners.map((b) => (
        <div
          key={b.id}
          role="status"
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
            TONE_STYLES[b.tone] ?? TONE_STYLES.info
          }`}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{b.title}</p>
            <div
              className="prose-sm mt-0.5 text-sm [&_a]:underline"
              // Body is sanitized server-side before persistence.
              dangerouslySetInnerHTML={{ __html: b.bodyHtml }}
            />
          </div>
          <button
            type="button"
            onClick={() => dismiss.mutate(b.id)}
            aria-label="Dismiss"
            className="shrink-0 rounded p-1 opacity-60 transition-opacity hover:opacity-100"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
